import { useMutation } from "@tanstack/react-query";
import { getBackendUrl } from "api/common";
import { mutationFnPOST } from "api/tanStackQuery/helpers";
import { AffectedTrain } from "features/CenterContent/RoleContent/Vaktleder/types";
import { TrainInfoState } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/types/trainInformationAggregated";
import { CloseTrainsModal } from "features/VehicleSidebar/AffectedTrains/CloseTrainsModal";
import {
  AffectedTrainFilter,
  TrainFilter,
} from "features/VehicleSidebar/AffectedTrains/TrainFilter";
import HandledTrainListElement from "features/VehicleSidebar/AffectedTrains/lists/HandledTrainListElement";
import { ButtonGroupFitter } from "features/VehicleSidebar/AffectedTrains/styles";
import { CloseTrainInformationBatchBody } from "features/VehicleSidebar/AffectedTrains/types";
import { sortByInfoUpdatedAt } from "features/VehicleSidebar/AffectedTrains/utils/AffectedTrainSorter";
import { getFilteredTrains } from "features/VehicleSidebar/AffectedTrains/utils/filterAffectedTrains";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Checkbox, DropsButton, Flex } from "shared/components/Spor";
import { FailureMessage } from "shared/components/feedback/FailureMessage/FailureMessage";
import { NoDataMessage } from "shared/components/feedback/NoDataMessage/NoDataMessage";
import { useInfiniteScroll } from "shared/hooks/useInfiniteScroll";
import { useLoadingWithTimeout } from "shared/hooks/useLoadingWithTimeout";

type HandledTrainsProps = {
  trains: AffectedTrain[];
  showFilter: boolean;
};
const HandledTrains: FC<HandledTrainsProps> = ({ trains, showFilter }) => {
  const [filter, setFilter] = useState<AffectedTrainFilter>(null);
  const [selectedTrainIds, setSelectedTrainIds] = useState<AffectedTrain[]>([]);
  const [waitingForUpdatedEvents, setWaitingForUpdatedEvents] = useState(false);
  const [modalOpen, setModalOpen] = useState<boolean>(false);

  const { t } = useTranslation();
  const {
    mutate: closeAffectedTrainsStatus,
    isError,
    error,
    isPending,
    isSuccess,
    reset: resetMutationState,
  } = useMutation({
    mutationFn: (body: CloseTrainInformationBatchBody) =>
      mutationFnPOST<string, CloseTrainInformationBatchBody>(
        `${getBackendUrl()}/trainInformation/information/close`,
        body,
      ),
  });

  const selectTrain = (trainId: AffectedTrain) => {
    if (selectedTrainIds.includes(trainId)) {
      setSelectedTrainIds(selectedTrainIds.filter((it) => it !== trainId));
    } else {
      setSelectedTrainIds([...selectedTrainIds, trainId]);
    }
  };

  const filteredTrains = getFilteredTrains(filter, trains);

  // On data update and successfully finished mutation
  useEffect(() => {
    if (isSuccess) {
      setWaitingForUpdatedEvents(false);
      resetMutationState();
      setSelectedTrainIds([]);
    }
  }, [trains]);

  useEffect(() => {
    if (isError) {
      setWaitingForUpdatedEvents(false);
      resetMutationState();
    }
  }, [selectedTrainIds]);

  const selectableTrains = useMemo(
    () =>
      filteredTrains.filter((train) => {
        const trainInfo = train.referencedOperationalTrainInformation.at(0);
        const newestVersion = trainInfo?.versions.at(0);
        const newestVersionopen = newestVersion?.state === TrainInfoState.OPEN;
        return newestVersionopen;
      }),
    [filteredTrains],
  );

  const {
    setloading: setCloseStatusloading,
    hasTimedOut: closeStatusHasTimedOut,
  } = useLoadingWithTimeout(30_000);

  useEffect(() => {
    if (!isPending) {
      setCloseStatusloading(false);
    }
  }, [isPending]);

  const closeTrains = () => {
    const uuids = selectedTrainIds
      .map(
        (it) =>
          it.referencedOperationalTrainInformation.at(0)?.latestInfoUuid ??
          null,
      )
      .filter(Boolean) as string[];
    closeAffectedTrainsStatus({ uuids });
    setCloseStatusloading(true);
    setWaitingForUpdatedEvents(true);
  };

  const containerRef = useRef<HTMLDivElement>(null);

  const { numberOfItemsToRender } = useInfiniteScroll({
    containerRef,
    initialItemsToRender: 30,
    maxItemsToRender: filteredTrains.length,
  });

  return (
    <>
      {modalOpen && (
        <CloseTrainsModal
          setSelectedTrainIds={setSelectedTrainIds}
          selectedTrainIds={selectedTrainIds}
          closeTrains={closeTrains}
          closeModal={() => setModalOpen(false)}
        />
      )}
      <Flex flexDir="column" height="100%">
        <TrainFilter
          showFilter={showFilter}
          trains={trains}
          filter={filter}
          setFilter={setFilter}
        />
        {(isError || closeStatusHasTimedOut) && (
          <FailureMessage
            customMessage={`Friskmelding mislyktes: ${error?.message ?? "Tidsavbrudd"}`}
          />
        )}
        {(isError || !waitingForUpdatedEvents) &&
          selectableTrains.length > 0 && (
            <ButtonGroupFitter>
              <Checkbox
                onCheckedChange={() => {
                  if (selectedTrainIds.length !== selectableTrains.length) {
                    setSelectedTrainIds(selectableTrains);
                  } else {
                    setSelectedTrainIds([]);
                  }
                }}
                checked={selectedTrainIds.length === selectableTrains.length}
                ml="12px" // to match other checkboxes
              >
                {t("Marker alle tog")}
              </Checkbox>
              {selectedTrainIds.length > 0 && (
                <DropsButton
                  size="xs"
                  variant="primary"
                  fontWeight="600"
                  onClick={() => {
                    setModalOpen(true);
                  }}
                >
                  Friskmeld {selectedTrainIds.length} tog
                </DropsButton>
              )}
            </ButtonGroupFitter>
          )}

        <Box
          flex="1"
          overflow="auto"
          ref={containerRef}
          h="calc(100% - 68px)"
          p="12px"
        >
          {filteredTrains.length > 0 ? (
            filteredTrains
              .toSorted(sortByInfoUpdatedAt)
              .slice(0, numberOfItemsToRender)
              .map((train) => (
                <HandledTrainListElement
                  train={train}
                  key={`${train.trainId.identifier}_${train.trainId.nominalDate}_${train.incidentId}`}
                  selectedTrainIds={selectedTrainIds}
                  selectTrain={selectTrain}
                  loading={
                    !isError &&
                    waitingForUpdatedEvents &&
                    selectedTrainIds.includes(train)
                  }
                />
              ))
          ) : (
            <NoDataMessage
              pt="24px"
              height="100%"
              reason="Filteret gir ingen treff"
            />
          )}
        </Box>
      </Flex>
    </>
  );
};

export default HandledTrains;
